'use client';

// react
import { useCallback, useEffect, useState } from 'react';
// @mui
import { Grid, Stack, Slide, Box } from '@mui/material';
// components
import { SplashScreen } from '@/components/minimal/loading-screen';
import { BaseSection } from '@/components/ui';
import { CardsSlider } from '@/components/common';
import { EventCard } from '@/components/common/cards';
// next
import dynamic from 'next/dynamic';
// sections
import {
  ContentDetails,
  ContentsSearchBar,
  ContentComments,
  ContentUserRatings,
} from '@/sections/common/contents';
const Error = dynamic(() => import('@/sections/common/Error'));
// hooks
import { useAuth, useFetch, useDialog } from '@/hooks';
// toast
import { toast } from 'react-toastify';
// enums
import { EVENT_REGISTERED } from '@/enums';
// services
import { ContentsService } from '@/services';
import { useParams } from 'next/navigation';

export default function EventDetailView() {
  const { user } = useAuth();

  const eventId = Number(useParams()?.eventId ?? 0);

  const { openDialog, closeDialog } = useDialog();

  const [commentsOpen, setCommentsOpen] = useState(false);
  const [eventDetail, setEventDetail] = useState<any>({});
  const [suggestedEvents, setSuggestedEvents] = useState([]);

  const fetchData = useCallback(async () => {
    const [
      { success: eventSuccess, data: eventData = null },
      { success: suggestedEventsSuccess, data: suggestedEventsData = null },
    ] = await Promise.all([
      ContentsService.getDetail(eventId),
      ContentsService.getSuggestions(eventId),
    ]);

    if (eventSuccess) {
      setEventDetail(eventData);
    } else return false;
    if (suggestedEventsSuccess) {
      setSuggestedEvents(suggestedEventsData);
    }
    return true;
  }, [eventId]);

  const onInvitationAccept = useCallback(async () => {
    const { success } = await ContentsService.postBuy(eventId);
    if (success) {
      toast.success('Invitation accepted successfully!');
      closeDialog();
      fetchData();
    }
  }, [closeDialog, eventId, fetchData]);

  useEffect(() => {
    if (eventDetail?.isInvited && eventDetail?.registered !== EVENT_REGISTERED.ACTIVE) {
      openDialog({
        title: 'You have a pending invitation for this event. Do you want to accept?',
        acceptText: 'Accept',
        onAccept: onInvitationAccept,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventDetail]);

  const { notFound, loading } = useFetch(fetchData, [user]);

  const toggleComments = () => {
    setCommentsOpen(!commentsOpen);
  };

  if (loading) return <SplashScreen />;
  if (notFound) return <Error statusCode={404} />;

  return (
    <Box sx={{ my: 2 }}>
      <Stack alignItems="center">
        <ContentsSearchBar type="events" />
        <Grid
          container
          px={{ xs: 1, md: 5 }}
          mb={8}
          spacing={2}
          flexDirection="row"
          justifyContent="center"
          alignItems="stretch"
        >
          <Grid item xs={12} md={8}>
            <ContentDetails
              content={eventDetail}
              mutate={fetchData}
              toggleComments={toggleComments}
            />
          </Grid>
          <Slide direction="up" in={commentsOpen} mountOnEnter unmountOnExit>
            <Grid item xs={12} md={4}>
              <ContentComments contentId={eventId} />
            </Grid>
          </Slide>
        </Grid>
      </Stack>
      <div id="ratings">
        <ContentUserRatings
          contentId={eventId}
          mutate={fetchData}
          userGroupId={eventDetail?.userGroupId}
          ratingActive={eventDetail?.userCanRate}
          ratingScore={eventDetail?.ratingScore}
          ratingCount={eventDetail?.totalRatingCount}
        />
      </div>
      <BaseSection backgroundColor="#ECECEC" height="auto">
        {suggestedEvents.length > 0 && (
          <CardsSlider
            title={'Event suggestions from the same category'}
            cards={suggestedEvents}
            CardComponent={EventCard}
          />
        )}
      </BaseSection>
    </Box>
  );
}
